import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  Info,
  Landmark,
  LoaderCircle,
  RefreshCw,
  Wallet,
  WalletCards,
} from "lucide-react";
import { type ReactNode, useId, useMemo, useRef, useState } from "react";
import { useWatch } from "react-hook-form";
import { z } from "zod";

import { OrganizationScopePicker } from "@/modules/dashboard/payments-config/components/OrganizationScopePicker";
import { useOrganizationsCatalog } from "@/modules/dashboard/payments-config/hooks/useOrganizationsCatalog";
import {
  ConsequenceList,
  ConsequenceRow,
} from "@/modules/dashboard/payout-batches/components/execute/ConsequenceList";
import { useArmDelay } from "@/modules/dashboard/payout-batches/hooks/useArmDelay";
import { useRuntimeWallets } from "@/modules/dashboard/wallets/hooks/useRuntimeWallets";
import type { RuntimeWallet } from "@/modules/dashboard/wallets/types/payments-wallets.types";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAmountDisplay } from "@/shared/components/data-display/ColocaAmountDisplay";
import { ColocaIsotype } from "@/shared/components/data-display/ColocaIsotype";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaAmountInput } from "@/shared/components/forms/ColocaAmountInput";
import { ColocaCheckbox } from "@/shared/components/forms/ColocaCheckbox";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaInputField } from "@/shared/components/forms/ColocaInputField";
import { ColocaSelectField } from "@/shared/components/forms/ColocaSelectField";
import { ColocaTextareaField } from "@/shared/components/forms/ColocaTextareaField";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import { ColocaMoneyTransit } from "@/shared/components/motion/ColocaMoneyTransit";
import { ColocaDialog } from "@/shared/components/overlays/ColocaDialog";
import { TwoFactorActionModals } from "@/shared/components/overlays/TwoFactorActionModals";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { readApiStatus } from "@/shared/utils/apiError";
import { formatCurrencyAmount, minorUnitsFor } from "@/shared/utils/currencyMinorUnits";

import { WALLET_TOP_UP_CONCEPT_MAX, WALLET_TOP_UP_NOTE_MAX } from "../constants/walletTopUps.constants";
import { UnresolvedWalletTopUpError, useCreateWalletTopUp } from "../hooks/useCreateWalletTopUp";
import type { WalletTopUpCreated, WalletTopUpTarget } from "../types/walletTopUps.types";
import { balanceAfterTopUp, topUpAmountProblem } from "../utils/walletTopUp";
import {
  isSameTopUp,
  mayHaveCredited,
  type WalletTopUpAttempt,
  WalletTopUpBusyError,
  type WalletTopUpLookup,
} from "../utils/walletTopUpAttempt";
import { WalletTopUpSuccess } from "./WalletTopUpSuccess";

const FORM_ID = "wallet-top-up-form";

export interface WalletTopUpDialogProps {
  /** Null closes the dialog. An organization (and a wallet) set here come preselected. */
  target: WalletTopUpTarget | null;
  onClose: () => void;
  onCreated?: (topUp: WalletTopUpCreated) => void;
}

export function WalletTopUpDialog({ target, onClose, onCreated }: WalletTopUpDialogProps) {
  if (!target) return null;
  return (
    <WalletTopUpDialogContent
      key={`${target.organization.id}:${target.walletId ?? ""}`}
      target={target}
      onClose={onClose}
      onCreated={onCreated}
    />
  );
}

/** Opened from the history page, where nothing is preselected. */
export function NewWalletTopUpDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated?: (topUp: WalletTopUpCreated) => void;
}) {
  if (!open) return null;
  return <WalletTopUpDialogContent target={null} onClose={onClose} onCreated={onCreated} />;
}

type Step = "form" | "review" | "done";

interface FormValues {
  wallet_id: string;
  amount: string;
  concept: string;
  internal_note: string;
}

interface ContentProps {
  target: WalletTopUpTarget | null;
  onClose: () => void;
  onCreated?: (topUp: WalletTopUpCreated) => void;
}

function WalletTopUpDialogContent({ target, onClose, onCreated }: ContentProps) {
  const { t, activeLanguage } = useTranslations("walletTopUps");
  const [organizationId, setOrganizationId] = useState<string | null>(target?.organization.id ?? null);
  const [step, setStep] = useState<Step>("form");
  const [submitted, setSubmitted] = useState<FormValues | null>(null);
  const [created, setCreated] = useState<WalletTopUpCreated | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [reviewed, setReviewed] = useState(false);
  // What the last check said about an unresolved top-up, and whether the receipt on screen is one it found.
  const [lookup, setLookup] = useState<WalletTopUpLookup | null>(null);
  const [checking, setChecking] = useState(false);
  const [recovered, setRecovered] = useState(false);
  const { armed, disarm } = useArmDelay(step === "review");
  const { submit, check, unresolved, twoFactor } = useCreateWalletTopUp(organizationId);
  const runtime = useRuntimeWallets(organizationId ?? undefined);

  const currencyRef = useRef<string | null>(null);
  const schema = useMemo(
    () =>
      z
        .object({
          wallet_id: z.string().min(1, t("validation_wallet")),
          amount: z.string(),
          concept: z.string().trim().min(1, t("validation_concept")).max(WALLET_TOP_UP_CONCEPT_MAX),
          internal_note: z.string().trim().min(1, t("validation_note")).max(WALLET_TOP_UP_NOTE_MAX),
        })
        .superRefine((values, ctx) => {
          const problem = topUpAmountProblem(values.amount, currencyRef.current ?? "COP");
          if (!problem) return;
          ctx.addIssue({
            code: "custom",
            path: ["amount"],
            message:
              problem === "precision"
                ? t("validation_amount_precision", {
                    currency: currencyRef.current ?? "",
                    digits: String(minorUnitsFor(currencyRef.current ?? "COP")),
                  })
                : t("validation_amount"),
          });
        }),
    [t],
  );
  const form = useZodForm(schema, {
    wallet_id: target?.walletId ?? "",
    amount: "",
    concept: t("concept_default"),
    internal_note: "",
  });

  const values = useWatch({ control: form.control });
  const walletId = values.wallet_id ?? "";
  const wallet = runtime.wallets.find((candidate) => candidate.id === walletId) ?? null;
  currencyRef.current = wallet?.currency ?? null;
  const organizationName =
    target?.organization.name ?? runtime.wallets[0]?.organization.name ?? t("organization_unknown");

  // The unresolved top-up is this very one: sending it again reuses its key, so it can only ever credit once.
  const isRetryOf = (attempt: WalletTopUpAttempt | null, body: Partial<FormValues> | null) =>
    !!attempt &&
    !!organizationId &&
    !!body &&
    isSameTopUp(attempt.payload, {
      organization_id: organizationId,
      wallet_id: body.wallet_id ?? "",
      amount: body.amount ?? "",
      concept: body.concept ?? "",
      internal_note: body.internal_note ?? "",
    });
  // Any other top-up to the organization waits until the unresolved one is checked.
  const blockedByUnresolved = !!unresolved && !isRetryOf(unresolved, values);

  const changeOrganization = (next: string | null) => {
    setOrganizationId(next);
    form.setValue("wallet_id", "");
    setError(null);
    setLookup(null);
  };

  const checkUnresolved = async () => {
    setChecking(true);
    setLookup(null);
    try {
      const result = await check();
      if (result.kind !== "placed") {
        setLookup(result.kind);
        return;
      }
      disarm();
      setCreated(result.topUp);
      setRecovered(true);
      setStep("done");
      onCreated?.(result.topUp);
    } finally {
      setChecking(false);
    }
  };

  const toReview = (next: FormValues) => {
    if (blockedByUnresolved) return;
    setSubmitted(next);
    setReviewed(false);
    setError(null);
    setStep("review");
  };

  const backToForm = () => {
    disarm();
    setStep("form");
    setError(null);
  };

  const confirm = async () => {
    if (!submitted || !organizationId) return;
    setSubmitting(true);
    setError(null);
    setLookup(null);
    try {
      const result = await submit(
        { organization_id: organizationId, ...submitted },
        wallet?.currency ?? null,
      );
      if (!result) return;
      setCreated(result);
      setRecovered(false);
      setStep("done");
      onCreated?.(result);
    } catch (err) {
      if (err instanceof UnresolvedWalletTopUpError) setError(t("unresolved_blocked"));
      // Nothing was sent: another tab held this organization's send for too long.
      else if (err instanceof WalletTopUpBusyError) setError(t("top_up_busy"));
      // No answer, or one that cannot rule the credit out: the unresolved notice says so and offers the check.
      else if (mayHaveCredited(readApiStatus(err))) setError(null);
      else setError(HttpApiService.getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const startAnother = () => {
    form.reset({ wallet_id: "", amount: "", concept: t("concept_default"), internal_note: "" });
    setCreated(null);
    setSubmitted(null);
    setRecovered(false);
    setLookup(null);
    disarm();
    setStep("form");
  };

  // The code left and the challenge closed: the request is crediting the wallet and emailing the receipt.
  const processing = step === "review" && twoFactor.isRunning && !twoFactor.challenge.open;

  const title =
    step === "done"
      ? t("dialog_title_done")
      : processing
        ? t("dialog_title_processing")
        : step === "review"
          ? t("dialog_title_review")
          : t("dialog_title");
  const description =
    step === "done"
      ? t("dialog_description_done")
      : processing
        ? t("dialog_description_processing")
        : step === "review"
          ? t("dialog_description_review")
          : t("dialog_description");

  return (
    <>
      <ColocaDialog
        open
        onOpenChange={(open) => {
          if (!open && !submitting) onClose();
        }}
        size="lg"
        title={title}
        description={description}
        showCloseButton={!submitting}
        footer={
          processing ? null : step === "form" ? (
            <>
              <ColocaButton variant="ghost" onClick={onClose}>
                {t("action_cancel")}
              </ColocaButton>
              <ColocaButton
                type="submit"
                form={FORM_ID}
                variant="primary"
                disabled={!wallet || blockedByUnresolved || checking}
              >
                {t("action_review")}
              </ColocaButton>
            </>
          ) : step === "review" ? (
            <>
              <ColocaButton variant="ghost" icon={ArrowLeft} onClick={backToForm} disabled={submitting}>
                {t("action_back")}
              </ColocaButton>
              <ColocaButton
                variant="primary"
                icon={Landmark}
                loading={submitting}
                disabled={!reviewed || !armed || submitting || checking}
                onClick={() => void confirm()}
              >
                {t("action_confirm")}
              </ColocaButton>
            </>
          ) : (
            <>
              <ColocaButton variant="ghost" onClick={startAnother}>
                {t("action_another")}
              </ColocaButton>
              <ColocaButton variant="primary" onClick={onClose}>
                {t("action_close")}
              </ColocaButton>
            </>
          )
        }
      >
        {step === "form" && (
          <ColocaForm id={FORM_ID} form={form} onSubmit={toReview} className="space-y-5">
            {!target && <OrganizationField value={organizationId} onChange={changeOrganization} />}
            {target && (
              <div className="flex items-center gap-3 rounded-xl border border-border bg-muted/30 px-4 py-3">
                <WalletCards className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">{t("field_organization")}</p>
                  <p className="truncate text-sm font-semibold text-foreground">{target.organization.name}</p>
                </div>
              </div>
            )}

            {unresolved && (
              <UnresolvedTopUpNotice
                attempt={unresolved}
                retry={!blockedByUnresolved}
                lookup={lookup}
                checking={checking}
                onCheck={() => void checkUnresolved()}
                locale={activeLanguage}
              />
            )}
            {!unresolved && lookup === "not_placed" && (
              <ColocaAlert variant="secondary" icon={Info} text={t("unresolved_not_placed")} />
            )}

            {organizationId && (
              <WalletField
                wallets={runtime.wallets}
                isLoading={runtime.isLoading}
                error={runtime.error}
                locale={activeLanguage}
              />
            )}

            {wallet && (
              <>
                <ColocaAmountInput
                  name="amount"
                  label={t("field_amount")}
                  description={t("field_amount_hint", { currency: wallet.currency })}
                  suffix={wallet.currency}
                  maxFractionDigits={minorUnitsFor(wallet.currency)}
                />
                <ColocaInputField
                  name="concept"
                  label={t("field_concept")}
                  description={t("field_concept_hint")}
                  maxLength={WALLET_TOP_UP_CONCEPT_MAX}
                  required
                />
                <ColocaTextareaField
                  name="internal_note"
                  label={t("field_note")}
                  description={t("field_note_hint")}
                  placeholder={t("field_note_placeholder")}
                  maxLength={WALLET_TOP_UP_NOTE_MAX}
                  rows={3}
                />
              </>
            )}

            {error && <ColocaAlert variant="destructive" icon={AlertCircle} text={error} />}
          </ColocaForm>
        )}

        {processing && submitted && (
          <TopUpProcessing organizationName={organizationName} wallet={wallet} amount={submitted.amount} />
        )}

        {step === "review" && !processing && submitted && wallet && (
          <TopUpReview
            organizationName={organizationName}
            wallet={wallet}
            values={submitted}
            reviewed={reviewed}
            onReviewedChange={setReviewed}
            disabled={submitting || checking}
            error={error}
            notice={
              unresolved && isRetryOf(unresolved, submitted) ? (
                <UnresolvedTopUpNotice
                  attempt={unresolved}
                  retry
                  lookup={lookup}
                  checking={checking}
                  onCheck={() => void checkUnresolved()}
                  locale={activeLanguage}
                />
              ) : null
            }
          />
        )}

        {step === "done" && created && <WalletTopUpSuccess topUp={created} recovered={recovered} />}
      </ColocaDialog>
      <TwoFactorActionModals
        controller={twoFactor}
        requiredDescription={t("two_factor_required")}
        challengeDescription={
          submitted && wallet
            ? t("two_factor_description", {
                amount: formatCurrencyAmount(submitted.amount, wallet.currency, activeLanguage),
                currency: wallet.currency,
                name: organizationName,
              })
            : undefined
        }
      />
    </>
  );
}

function OrganizationField({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (id: string | null) => void;
}) {
  const { t } = useTranslations("walletTopUps");
  const { t: tBatches } = useTranslations("payoutBatches");
  const pickerId = useId();
  const catalog = useOrganizationsCatalog();
  return (
    <OrganizationScopePicker
      id={pickerId}
      mode="required"
      value={value}
      onValueChange={onChange}
      catalog={catalog}
      labels={{
        label: t("field_organization"),
        placeholder: tBatches("org_picker_placeholder"),
        loading: tBatches("org_picker_loading"),
        empty: tBatches("org_picker_empty"),
        searching: tBatches("org_picker_searching"),
        searchError: tBatches("org_picker_search_error"),
        catalogError: tBatches("org_picker_catalog_error"),
        catalogTruncated: tBatches("org_picker_catalog_truncated"),
        requiredHint: t("field_organization_required_hint"),
        orgHint: t("field_organization_hint"),
      }}
    />
  );
}

interface WalletFieldProps {
  wallets: RuntimeWallet[];
  isLoading: boolean;
  error: string | null;
  locale: string;
}

function WalletField({ wallets, isLoading, error, locale }: WalletFieldProps) {
  const { t } = useTranslations("walletTopUps");
  if (isLoading) return <ColocaSkeleton className="h-16 w-full" />;
  if (error) return <ColocaAlert variant="destructive" icon={AlertCircle} text={error} />;
  if (wallets.length === 0) {
    return <ColocaAlert variant="warning" icon={Info} text={t("field_wallet_none")} />;
  }
  return (
    <ColocaSelectField
      name="wallet_id"
      label={t("field_wallet")}
      description={t("field_wallet_hint")}
      placeholder={t("field_wallet_placeholder")}
      required
      items={wallets.map((wallet) => ({
        value: wallet.id,
        disabled: wallet.status !== "active",
        label:
          wallet.status === "active"
            ? t("field_wallet_option", {
                currency: wallet.currency,
                balance: formatCurrencyAmount(wallet.balance_available, wallet.currency, locale),
              })
            : t("field_wallet_option_inactive", {
                currency: wallet.currency,
                status: t(wallet.status === "frozen" ? "wallet_frozen" : "wallet_closed"),
              }),
      }))}
    />
  );
}

interface TopUpReviewProps {
  organizationName: string;
  wallet: RuntimeWallet;
  values: FormValues;
  reviewed: boolean;
  onReviewedChange: (checked: boolean) => void;
  disabled: boolean;
  error: string | null;
  /** The unresolved notice, when this review is the retry of a top-up whose answer was lost. */
  notice?: ReactNode;
}

function TopUpReview({
  organizationName,
  wallet,
  values,
  reviewed,
  onReviewedChange,
  disabled,
  error,
  notice,
}: TopUpReviewProps) {
  const { t } = useTranslations("walletTopUps");
  const digits = minorUnitsFor(wallet.currency);
  const amountProps = { currency: wallet.currency, fractionDigits: digits };
  return (
    <div className="space-y-5">
      <ConsequenceList>
        <ConsequenceRow label={t("review_organization")} value={organizationName} />
        <ConsequenceRow
          label={t("review_wallet")}
          value={t("review_wallet_value", { currency: wallet.currency })}
        />
        <ConsequenceRow
          label={t("review_balance_before")}
          value={<ColocaAmountDisplay value={wallet.balance_available} {...amountProps} size="sm" />}
        />
        <ConsequenceRow
          label={t("review_amount")}
          value={
            <span className="text-primary">
              <ColocaAmountDisplay value={values.amount} {...amountProps} size="sm" signed />
            </span>
          }
        />
        <ConsequenceRow
          label={t("review_balance_after")}
          emphasis
          value={
            <ColocaAmountDisplay
              value={balanceAfterTopUp(wallet.balance_available, values.amount)}
              {...amountProps}
              size="lg"
            />
          }
        />
      </ConsequenceList>

      <dl className="grid gap-3 text-sm sm:grid-cols-2">
        <div className="min-w-0">
          <dt className="text-xs text-muted-foreground">{t("review_concept")}</dt>
          <dd className="mt-0.5 break-words font-medium text-foreground">{values.concept.trim()}</dd>
        </div>
        <div className="min-w-0">
          <dt className="text-xs text-muted-foreground">{t("review_note")}</dt>
          <dd className="mt-0.5 break-words text-foreground">{values.internal_note.trim()}</dd>
        </div>
      </dl>

      <ColocaAlert variant="secondary" icon={Info} text={t("review_client_sees")} />
      <ColocaAlert variant="warning" icon={AlertTriangle} text={t("review_irreversible")} />
      {notice}
      {error && <ColocaAlert variant="destructive" icon={AlertCircle} text={error} />}

      <ColocaCheckbox
        checked={reviewed}
        onCheckedChange={onReviewedChange}
        disabled={disabled}
        label={t("review_checkbox")}
      />
    </div>
  );
}

interface TopUpProcessingProps {
  organizationName: string;
  wallet: RuntimeWallet | null;
  amount: string;
}

/** While the authorized top-up is on its way: the money travelling from Coloca to the client's wallet. */
function TopUpProcessing({ organizationName, wallet, amount }: TopUpProcessingProps) {
  const { t, activeLanguage } = useTranslations("walletTopUps");
  if (!wallet) {
    return (
      <div role="status" className="flex justify-center py-10">
        <LoaderCircle aria-hidden="true" className="size-8 animate-spin text-primary" />
      </div>
    );
  }
  const walletLabel = t("review_wallet_value", { currency: wallet.currency });
  return (
    <div role="status" className="flex flex-col items-center gap-6 py-6 text-center">
      <ColocaMoneyTransit
        className="max-w-md"
        label={t("processing_transit_aria", {
          amount: formatCurrencyAmount(amount, wallet.currency, activeLanguage),
          currency: wallet.currency,
          name: organizationName,
        })}
        from={{ icon: <ColocaIsotype className="size-6" />, label: t("processing_from"), tone: "brand" }}
        to={{ icon: <Wallet className="size-5" />, label: organizationName, caption: walletLabel }}
        legs={[{ rail: { kind: "fiat" }, asset: { kind: "fiat", currency: wallet.currency } }]}
      />
      <div className="text-primary">
        <ColocaAmountDisplay
          value={amount}
          currency={wallet.currency}
          fractionDigits={minorUnitsFor(wallet.currency)}
          size="lg"
          signed
        />
      </div>
    </div>
  );
}

interface UnresolvedTopUpNoticeProps {
  attempt: WalletTopUpAttempt;
  /** The form holds that same top-up: authorizing it again is safe. */
  retry: boolean;
  lookup: WalletTopUpLookup | null;
  checking: boolean;
  onCheck: () => void;
  locale: string;
}

/** A top-up to this organization whose answer was lost: it may have credited, so it is checked before another. */
function UnresolvedTopUpNotice({
  attempt,
  retry,
  lookup,
  checking,
  onCheck,
  locale,
}: UnresolvedTopUpNoticeProps) {
  const { t } = useTranslations("walletTopUps");
  const { payload, currency } = attempt;
  const amount = currency
    ? `${formatCurrencyAmount(payload.amount, currency, locale)} ${currency}`
    : payload.amount;
  const status =
    lookup === "pending" ? t("unresolved_pending") : lookup === "unknown" ? t("unresolved_unknown") : null;
  return (
    <ColocaAlert
      variant="warning"
      icon={AlertTriangle}
      heading={t("unresolved_title")}
      text={
        <>
          <span className="block">
            {retry ? t("unresolved_text_retry") : t("unresolved_text", { amount, concept: payload.concept })}
          </span>
          {status && (
            <span className="mt-1.5 block font-medium" role="status">
              {status}
            </span>
          )}
        </>
      }
      actions={
        <ColocaButton size="sm" variant="secondary" icon={RefreshCw} loading={checking} onClick={onCheck}>
          {t("unresolved_action")}
        </ColocaButton>
      }
    />
  );
}
